// Intake Intelligence duck slice, extracted from DataEntryDetailsDucks.ts
// (Spec #34098 / F1a, AC17 - tidy-first structural refactor #5).
//
// Extraction changes only file location, not state shape, action-type
// strings, or action-creator signatures (Business Rule 18):
//   - state.dataEntryState.requestAnalysis / getIntakeIntelligenceStatus /
//     requestIntakeIntelligenceStatus / intakeFeedbackSubmitStatus keep
//     their existing flat shape - intakeDucksReducer is a DELEGATING
//     reducer called from DataEntryDetailsDucks.ts's main `dataEntryState`
//     reducer's `default` case, not a combineReducers composition (which
//     would nest state under a new key and break every existing direct
//     state read across the app).
//   - Action-type string VALUES are unchanged (IntakeActionConstants below
//     mirrors the literal strings already used by DataEntryActionConstants
//     in DataEntryDetailsDucks.ts, so takeLatest registrations and any
//     existing `DataEntryActionConstants.getIntakeIntelligence*` reference
//     keep matching the same dispatched actions).

import { call, put } from "redux-saga/effects";
import { AxiosResponse } from "axios";
import { API_INITIAL, API_FETCHING, API_SUCCESS, API_FAILURE, ReduxApiStatus } from "../../common/constants/ApiConstants";
import { IntakeFromApiResponse, RequestAnalysis, RequestAnalysisData } from "../../models/intake";
import { QuickEntryCorrectionPayload } from "./dataEntryComponents/QuickEntryCorrections";
import {
    getIntakeIntelligenceStatusApi,
    requestIntakeIntelligenceApi,
    intakeIntelligenceFeedbackApi,
    commitQuickEntryApi,
} from "./test/DataEntry.APIs";

/** API_ERROR_STATE lives in DataEntryDetailsDucks.ts's DataEntryActionConstants; duplicated here (same literal) to avoid a circular import. */
const API_ERROR_STATE = "API_ERROR_STATE";

export const IntakeActionConstants = {
    getIntakeIntelligenceRequest: "getIntakeIntelligenceRequest",
    getIntakeIntelligenceSuccess: "getIntakeIntelligenceSuccess",
    getIntakeIntelligenceFailure: "getIntakeIntelligenceFailure",
    requestIntakeIntelligenceRequest: "requestIntakeIntelligenceRequest",
    requestIntakeIntelligenceSuccess: "requestIntakeIntelligenceSuccess",
    requestIntakeIntelligenceFailure: "requestIntakeIntelligenceFailure",
    feedbackForIntakeFieldRequest: "feedbackForIntakeFieldRequest",
    feedbackForIntakeFieldSuccess: "feedbackForIntakeFieldSuccess",
    feedbackForIntakeFieldFailure: "feedbackForIntakeFieldFailure",
    // Spec #34100 / F1b-rois (AC2): clear the prior analysis before an
    // auto re-extract when the request document is replaced.
    resetIntakeIntelligence: "resetIntakeIntelligence",
    // Spec #34102 (F1d): Confirm-All bulk correction commit.
    commitQuickEntryRequest: "commitQuickEntryRequest",
    commitQuickEntrySuccess: "commitQuickEntrySuccess",
    commitQuickEntryFailure: "commitQuickEntryFailure",
} as const;

export type IntakeFeedbackApiStatus = {
    readonly [key in keyof RequestAnalysisData]?: ReduxApiStatus;
};

export interface IntakeDucksState {
    readonly requestAnalysis?: RequestAnalysis;
    readonly getIntakeIntelligenceStatus: ReduxApiStatus;
    readonly requestIntakeIntelligenceStatus: ReduxApiStatus;
    readonly intakeFeedbackSubmitStatus: IntakeFeedbackApiStatus;
    /** Spec #34102 (F1d): Confirm-All bulk-commit round-trip status. */
    readonly commitQuickEntryStatus: ReduxApiStatus;
    /** True when the most recent commitQuickEntryFailure was a 409 (write-once guard already tripped) - distinct from a generic network/server error (AC6, Test 5.2). */
    readonly commitQuickEntryConflict: boolean;
}

export const intakeDucksInitialState: IntakeDucksState = {
    requestAnalysis: undefined,
    getIntakeIntelligenceStatus: API_INITIAL,
    requestIntakeIntelligenceStatus: API_INITIAL,
    intakeFeedbackSubmitStatus: {},
    commitQuickEntryStatus: API_INITIAL,
    commitQuickEntryConflict: false,
};

export interface IntakeIntelligenceRequestAction {
    type: typeof IntakeActionConstants.getIntakeIntelligenceRequest |
        typeof IntakeActionConstants.requestIntakeIntelligenceRequest;
    organizationId: number;
    requestBufferId: number;
    onSuccess?: () => void;
}

export interface IntakeFeedback {
    facilityId: number;
    requestBufferId: number;
    feedbackItem: keyof RequestAnalysisData;
    feedback: boolean | null;
}

export interface SetFeedbackForIntakeFieldAction {
    type: typeof IntakeActionConstants.feedbackForIntakeFieldRequest;
    data: IntakeFeedback;
}

/**
 * Load existing intake intelligence data if it exists.
 */
export function getIntakeIntelligenceActionCreator(
    organizationId: number,
    requestBufferId: number,
    onSuccess?: () => void,
): IntakeIntelligenceRequestAction {
    return {
        type: IntakeActionConstants.getIntakeIntelligenceRequest,
        organizationId,
        requestBufferId,
        onSuccess,
    };
}

/**
 * Get intake intelligence data, generating it if necessary.
 */
export function requestIntakeIntelligenceActionCreator(
    organizationId: number,
    requestBufferId: number,
    onSuccess?: () => void,
): IntakeIntelligenceRequestAction {
    return {
        type: IntakeActionConstants.requestIntakeIntelligenceRequest,
        organizationId,
        requestBufferId,
        onSuccess,
    };
}

export function setFeedbackForIntakeFieldActionCreator(feedback: IntakeFeedback): SetFeedbackForIntakeFieldAction {
    return {
        type: IntakeActionConstants.feedbackForIntakeFieldRequest,
        data: feedback,
    };
}

export interface ResetIntakeIntelligenceAction {
    type: typeof IntakeActionConstants.resetIntakeIntelligence;
}

/**
 * Clears the prior analysis from the store (Spec #34100 / F1b-rois, AC2).
 * Dispatched before an auto re-extract when the request document is
 * replaced, so stale confidence/grounding disappears immediately rather
 * than lingering until the fresh extraction completes. Read-time/transient
 * only - touches no persisted state (AC11).
 */
export function resetIntakeIntelligenceActionCreator(): ResetIntakeIntelligenceAction {
    return {
        type: IntakeActionConstants.resetIntakeIntelligence,
    };
}

/**
 * Confirm-All bulk correction commit (Spec #34102 / F1d, AC4). The backend
 * (IntakeController.CommitQuickEntryAsync) distinguishes three failure shapes,
 * so the client maps each to its own callback for a meaningful message:
 *  - onConflict: 409 (QuickEntryAlreadyCommittedException - write-once guard
 *    already tripped; AC6/Test 5.2).
 *  - onNoIntakeRecord: 404 (QuickEntryNoIntakeRecordException - no RequestIntake
 *    row exists, i.e. intake extraction never produced one for this
 *    transaction). Added when the backend's 404 outcome (combo af935bf) was
 *    coalesced with F1d's client - previously a 404 fell through to onError's
 *    generic toast, masking a distinct, actionable state.
 *  - onError: any other network/server failure (Test 5.1).
 */
/**
 * F1c AC16 (AB#34101): the resolved patient/requestor match outcome sent with the
 * Confirm-All commit so the server persists it on the intake row. Optional - a
 * commit that omits it behaves exactly as before (the server COALESCEs NULLs).
 * camelCase keys bind to the QuickEntryCommitRequest DTO's PascalCase properties
 * (ASP.NET model binding is case-insensitive). NOT PHI (method label + a count).
 */
export interface QuickEntryMatchSummaryPayload {
    patientMatchMethod?: string;
    patientMatchCount?: number;
    requestorMatchMethod?: string;
    requestorMatchCount?: number;
}

export interface CommitQuickEntryAction {
    type: typeof IntakeActionConstants.commitQuickEntryRequest;
    organizationId: number;
    requestBufferId: number;
    corrections: readonly QuickEntryCorrectionPayload[];
    onSuccess?: (completedDate: string) => void;
    onConflict?: () => void;
    onNoIntakeRecord?: () => void;
    onError?: () => void;
    matchSummary?: QuickEntryMatchSummaryPayload;
}

export function commitQuickEntryActionCreator(
    organizationId: number,
    requestBufferId: number,
    corrections: readonly QuickEntryCorrectionPayload[],
    onSuccess?: (completedDate: string) => void,
    onConflict?: () => void,
    onNoIntakeRecord?: () => void,
    onError?: () => void,
    matchSummary?: QuickEntryMatchSummaryPayload,
): CommitQuickEntryAction {
    return {
        type: IntakeActionConstants.commitQuickEntryRequest,
        organizationId,
        requestBufferId,
        corrections,
        onSuccess,
        onConflict,
        onNoIntakeRecord,
        onError,
        matchSummary,
    };
}

/**
 * Delegating reducer for the 9 Intake Intelligence action types. Called from
 * DataEntryDetailsDucks.ts's main `dataEntryState` reducer's `default` case -
 * returns `state` unchanged for any action type it doesn't own, so it is
 * always safe to call unconditionally from that `default` branch.
 */
export function intakeDucksReducer<S extends IntakeDucksState>(state: S, action: any): S {
    switch (action.type) {
        case IntakeActionConstants.getIntakeIntelligenceRequest:
            return {
                ...state,
                getIntakeIntelligenceStatus: API_FETCHING,
            } as S;
        case IntakeActionConstants.getIntakeIntelligenceSuccess:
            return {
                ...state,
                getIntakeIntelligenceStatus: API_SUCCESS,
                requestAnalysis: action.data,
            } as S;
        case IntakeActionConstants.getIntakeIntelligenceFailure:
            return {
                ...state,
                getIntakeIntelligenceStatus: API_FAILURE,
            } as S;
        case IntakeActionConstants.requestIntakeIntelligenceRequest:
            return {
                ...state,
                requestIntakeIntelligenceStatus: API_FETCHING,
            } as S;
        case IntakeActionConstants.requestIntakeIntelligenceSuccess:
            return {
                ...state,
                requestIntakeIntelligenceStatus: API_SUCCESS,
                requestAnalysis: action.data,
            } as S;
        case IntakeActionConstants.requestIntakeIntelligenceFailure:
            return {
                ...state,
                requestIntakeIntelligenceStatus: API_FAILURE,
            } as S;
        case IntakeActionConstants.feedbackForIntakeFieldRequest: {
            const data: IntakeFeedback = action.data;
            return {
                ...state,
                intakeFeedbackSubmitStatus: {
                    ...state.intakeFeedbackSubmitStatus,
                    [data.feedbackItem]: API_FETCHING,
                },
            } as S;
        }
        case IntakeActionConstants.feedbackForIntakeFieldSuccess: {
            const data: IntakeFeedback = action.data;
            return {
                ...state,
                intakeFeedbackSubmitStatus: {
                    ...state.intakeFeedbackSubmitStatus,
                    [data.feedbackItem]: API_SUCCESS,
                },
                requestAnalysis: state.requestAnalysis && {
                    ...state.requestAnalysis,
                    dataApprovals: {
                        ...state.requestAnalysis.dataApprovals,
                        [data.feedbackItem]: data.feedback,
                    },
                },
            } as S;
        }
        case IntakeActionConstants.feedbackForIntakeFieldFailure: {
            const data: IntakeFeedback = action.data;
            return {
                ...state,
                intakeFeedbackSubmitStatus: {
                    ...state.intakeFeedbackSubmitStatus,
                    [data.feedbackItem]: API_FAILURE,
                },
            } as S;
        }
        case IntakeActionConstants.resetIntakeIntelligence:
            return {
                ...state,
                requestAnalysis: undefined,
                getIntakeIntelligenceStatus: API_INITIAL,
                requestIntakeIntelligenceStatus: API_INITIAL,
                intakeFeedbackSubmitStatus: {},
            } as S;
        case IntakeActionConstants.commitQuickEntryRequest:
            return {
                ...state,
                commitQuickEntryStatus: API_FETCHING,
                commitQuickEntryConflict: false,
            } as S;
        case IntakeActionConstants.commitQuickEntrySuccess:
            return {
                ...state,
                commitQuickEntryStatus: API_SUCCESS,
                commitQuickEntryConflict: false,
            } as S;
        case IntakeActionConstants.commitQuickEntryFailure:
            return {
                ...state,
                commitQuickEntryStatus: API_FAILURE,
                commitQuickEntryConflict: Boolean(action.conflict),
            } as S;
        default:
            return state;
    }
}

/**
 * AB#35289: stamp the request identity (facility id + request buffer id) onto the mapped analysis,
 * fill-if-missing, so the ownership check (analysisBelongsToTransaction, models/intake.ts) can pass
 * even when the backend response body omits the ids. Without this, the stored requestAnalysis has no
 * facilityId/requestBufferId, DataEntryDetails.ownedRequestAnalysis() returns undefined, the Quick
 * Entry seed (seedRequestTypePurpose) never writes REQUEST_TYPE/PURPOSE_TYPE, and the purpose-gated
 * rows (Records Requested / Service Date / Sensitive Info) stay "Not applicable yet". A response that
 * DOES send the ids is left untouched (?? fill-if-missing). IntakeFromApiResponse may return undefined.
 */
function stampAnalysisOwnership(
    analysis: ReturnType<typeof IntakeFromApiResponse>,
    action: IntakeIntelligenceRequestAction,
): ReturnType<typeof IntakeFromApiResponse> {
    return analysis && {
        ...analysis,
        facilityId: analysis.facilityId ?? action.organizationId,
        requestBufferId: analysis.requestBufferId ?? action.requestBufferId,
    };
}

/**
 * A 404 from an intake endpoint means the server-side feature gate is off, not a
 * failure worth surfacing: swallow it after the *Failure action rather than raising
 * the generic API_ERROR_STATE toast. Ported from DataEntryDetailsDucks.ts, where these
 * three sagas used to live (VRM 2026.08 HF).
 */
function intakeFeatureDisabledError(err: any): boolean {
    return typeof err === "object" &&
        err !== null &&
        "response" in err &&
        err.response?.status === 404;
}

/**
 * Load existing intake intelligence data without generating any new data.
 * @param action
 */
export function* getIntakeIntelligenceSaga(action: IntakeIntelligenceRequestAction) {
    try {
        const response: AxiosResponse = yield call(
            getIntakeIntelligenceStatusApi,
            action.organizationId,
            action.requestBufferId,
        );
        yield put({
            type: IntakeActionConstants.getIntakeIntelligenceSuccess,
            // AB#35289: stamp ownership (facilityId/requestBufferId) so QE seeds Type/Purpose.
            data: stampAnalysisOwnership(IntakeFromApiResponse(response.data), action),
        });
        try {
            // Try/catch so this can't fail the whole saga
            action.onSuccess?.();
        } catch (err) {
            console.error(err);
        }
    } catch (err) {
        console.error(err);
        yield put({ type: IntakeActionConstants.getIntakeIntelligenceFailure });
        if (intakeFeatureDisabledError(err)) {
            console.warn("Intake Intelligence endpoint returned 404, likely feature-gated server-side.");
            return;
        }
        if (typeof err === "object" && err !== null && "response" in err) {
            yield put({ type: API_ERROR_STATE, data: err.response });
        }
    }
}

/**
 * Load intake intelligence data, generating it if necessary.
 * @param action
 */
export function* requestIntakeIntelligenceSaga(action: IntakeIntelligenceRequestAction) {
    try {
        const response: AxiosResponse = yield call(
            requestIntakeIntelligenceApi,
            action.organizationId,
            action.requestBufferId,
        );
        yield put({
            type: IntakeActionConstants.requestIntakeIntelligenceSuccess,
            // AB#35289: stamp ownership (facilityId/requestBufferId) so QE seeds Type/Purpose.
            data: stampAnalysisOwnership(IntakeFromApiResponse(response.data), action),
        });
        try {
            // Try/catch so this can't fail the whole saga
            action.onSuccess?.();
        } catch (err) {
            console.error(err);
        }
    } catch (err) {
        console.error(err);
        yield put({ type: IntakeActionConstants.requestIntakeIntelligenceFailure });
        if (intakeFeatureDisabledError(err)) {
            console.warn("Intake Intelligence endpoint returned 404, likely feature-gated server-side.");
            return;
        }
        if (typeof err === "object" && err !== null && "response" in err) {
            // Specific handling for intake timeout - print specific error message
            if (err.response.data.detail === "INTAKE_TIMEOUT")
                err.response.statusText = "The automated intake process could not complete within the expected time (100 seconds). Please proceed with manual data entry for this request.";

            yield put({ type: API_ERROR_STATE, data: err.response });
        }
    }
}

export function* setFeedbackForIntakeFieldSaga(action: SetFeedbackForIntakeFieldAction) {
    try {
        yield call(
            intakeIntelligenceFeedbackApi,
            action.data.facilityId,
            action.data.requestBufferId,
            action.data.feedbackItem,
            action.data.feedback,
        );
        yield put({
            type: IntakeActionConstants.feedbackForIntakeFieldSuccess,
            data: action.data,
        });
    } catch (err) {
        console.error(err);
        yield put({
            type: IntakeActionConstants.feedbackForIntakeFieldFailure,
            data: action.data,
        });

        if (intakeFeatureDisabledError(err)) {
            console.warn("Intake Intelligence endpoint returned 404, likely feature-gated server-side.");
            return;
        }
        if (typeof err === "object" && err !== null && "response" in err) {
            yield put({ type: API_ERROR_STATE, data: err.response });
        }
    }
}

/**
 * Confirm-All bulk correction commit (Spec #34102 / F1d, AC4/AC5/AC6). A 409
 * response (write-once guard already tripped) is distinguished from a
 * generic failure so the caller can surface a distinct "already submitted"
 * message (Test 5.2) instead of the generic network-error toast (Test 5.1) -
 * neither path logs action.corrections' AiValue/SpecialistValue contents
 * (HIPAA - .claude/rules/hipaa-compliance.md), only the caught error object.
 */
export function* commitQuickEntrySaga(action: CommitQuickEntryAction) {
    try {
        const response: AxiosResponse = yield call(
            commitQuickEntryApi,
            action.organizationId,
            action.requestBufferId,
            action.corrections,
            action.matchSummary,
        );
        yield put({ type: IntakeActionConstants.commitQuickEntrySuccess });
        try {
            action.onSuccess?.(response?.data?.completedDate);
        } catch (err) {
            console.error(err);
        }
    } catch (err) {
        console.error(err);
        const status = typeof err === "object" && "response" in err ? (err as any).response?.status : undefined;
        const isConflict = status === 409;
        const isNoIntakeRecord = status === 404;
        yield put({ type: IntakeActionConstants.commitQuickEntryFailure, conflict: isConflict });
        try {
            if (isConflict) {
                action.onConflict?.();
            } else if (isNoIntakeRecord) {
                // 404: no RequestIntake row exists (combo af935bf backend outcome).
                // Distinct from the write-once conflict and from a generic error.
                action.onNoIntakeRecord?.();
            } else {
                action.onError?.();
            }
        } catch (callbackErr) {
            console.error(callbackErr);
        }
        if (typeof err === "object" && "response" in err) {
            yield put({ type: API_ERROR_STATE, data: (err as any).response });
        }
    }
}
